import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

export interface INocPassport {
  id: number;
  referenceNumber: string;
  empID: string;
  employeeName: string;
  fatherName: string;
  placeOfPosting: string;
  nOCAppliedFor: string;
  durationOfVisit: string;
  tentativeDate: string | null;
  requestStatus: string | null;
  requestCreationDate: string | null;
  presentAddress: string;
  permanentAddress: string;
  contactNumber: string;
  dnARPenaltyEffecti: string;
  dnARStatus: string;
  vigilancePending: string;
  dnARPending: string;
  pendingCase: string;
  passportType: string;
  passportNumber: string;
  placeOfVisitNOC: string;
  purposeOfVisit: string;
  birthdate: string | null;
  departmentName: string;
  remarks: string;
  hRemarks: string;
  approverLevel: string | null;
  approverUserID: string | null;
  approverName: string | null;
  workflowID: string | null;
  status: string;
  is_draft: boolean;
  created_at: string;
  modified_at: string;
}

interface INocPassportState {
  data: INocPassport[];
  passport: INocPassport | null; // Keep for backward compatibility
  currentPassport: INocPassport | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  deleteLoading: boolean;
  total: number;
  page: number;
  limit: number;
}

const initialState: INocPassportState = {
  data: [],
  passport: null, // Keep for backward compatibility
  currentPassport: null,
  loading: false,
  error: null,
  createLoading: false,
  deleteLoading: false,
  total: 0,
  page: 1,
  limit: 10,
};

// Fetch all passport/visa requests
export const getAllNocPassport = createAsyncThunk(
  "nocPassport/getAll",
  async (
    payload: {
      query?: string;
      page?: number;
      limit?: number;
      orderColumn?: string;
      orderBy?: string;
      requestStatus?: string;
      startDate?: string;
      endDate?: string;
    },
    thunkAPI
  ) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.FIND_ALL_PASSPORT_VISA,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Fetch single passport/visa request
export const getNocPassport = createAsyncThunk(
  "nocPassport/getOne",
  async (payload: { id: number | string }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.FIND_ONE_PASSPORT_VISA,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Create or update passport/visa request
export const createOrUpdateNocPassport = createAsyncThunk(
  "nocPassport/createOrUpdate",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.CREATE_OR_UPDATE_PASSPORT_VISA,
        payload
      );
      return response.data;
    } catch (error: any) {
      console.log("Error in createOrUpdateNocPassport:", error);
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Delete passport/visa request
export const deleteNOCPassportRequest = createAsyncThunk(
  "nocPassport/delete",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.DELETE_PASSPORT_VISA,
        payload
      );
      return { ...response.data, deletedId: payload.id };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Approve/Reject passport/visa request
export const approveRejectNocPassport = createAsyncThunk(
  "nocPassport/approveReject",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.APPROVE_REJECT_PASSPORT_VISA,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const nocPassportSlice = createSlice({
  name: "nocPassport",
  initialState,
  reducers: {
    resetNocPassportState: (state) => {
      Object.assign(state, initialState);
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentRequest: (state, action: PayloadAction<number>) => {
      const requestId = action.payload;
      const request = state.data.find((req) => req.id === requestId);
      state.passport = request || null; // Keep for backward compatibility
      state.currentPassport = request || null;
    },
    clearCurrentRequest: (state) => {
      state.passport = null; // Keep for backward compatibility
      state.currentPassport = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(getAllNocPassport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllNocPassport.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload.data || {};
        state.data = responseData.data || [];
        state.total = responseData.total || 0;
        state.page = responseData.page || 1;
        state.limit = responseData.limit || 10;
      })
      .addCase(getAllNocPassport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
      })
      // Fetch one
      .addCase(getNocPassport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNocPassport.fulfilled, (state, action) => {
        state.loading = false;
        state.passport = action.payload.data || null; // Keep for backward compatibility
        state.currentPassport = action.payload.data || null;
      })
      .addCase(getNocPassport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.passport = null; // Keep for backward compatibility
        state.currentPassport = null;
      })
      // Create or update
      .addCase(createOrUpdateNocPassport.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createOrUpdateNocPassport.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createOrUpdateNocPassport.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteNOCPassportRequest.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteNOCPassportRequest.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.data = state.data.filter(
          (req) => req.id !== action.payload.deletedId
        );
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteNOCPassportRequest.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      })
      // Approve/Reject
      .addCase(approveRejectNocPassport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveRejectNocPassport.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveRejectNocPassport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  resetNocPassportState,
  clearError,
  setCurrentRequest,
  clearCurrentRequest,
} = nocPassportSlice.actions;
export default nocPassportSlice.reducer;
